import React, { useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Heart, ShoppingBag, Sparkles, Check, ChevronRight } from "lucide-react";
import serumImg from "../assests/faceimg.webp";

interface CollectionInfo {
  title: string;
  subtitle: string;
  bannerTag: string;
}

interface Product {
  id: string;
  name: string;
  categoryKey: string;
  categoryName: string;
  price: number;
  rating: number;
  image: string;
  tag: string;
  ingredients: string;
}

const collectionDetails: Record<string, CollectionInfo> = {
  serums: {
    title: "Serums & Facial Oils",
    subtitle: "Potent formulations infused with Niacinamide, Peptides, and Hyaluronic Acid.",
    bannerTag: "Targeted Care",
  },
  moisturizers: {
    title: "Hydrating Moisturizers",
    subtitle: "Lightweight to rich creams engineered to lock in deep hydration.",
    bannerTag: "Deep Hydration",
  },
  "barrier-cream": {
    title: "Ceramide Barrier Creams",
    subtitle: "Intensive repair formulations designed to strengthen compromised skin barriers.",
    bannerTag: "Skin Repair",
  },
  cleansers: {
    title: "Cleansers & Toners",
    subtitle: "Gentle double-cleansing essentials and hydrating essences.",
    bannerTag: "Daily Prep",
  },
  default: {
    title: "Seoul Inspired Collections",
    subtitle: "Discover our full luxury suite of dermatologist-tested K-Beauty rituals.",
    bannerTag: "All Collections",
  },
};

const allCollectionProducts: Product[] = [
  {
    id: "glow-serum",
    name: "Glow Niacinamide Serum",
    categoryKey: "serums",
    categoryName: "Serums & Oils",
    price: 48.0,
    rating: 4.9,
    image: serumImg,
    tag: "Bestseller",
    ingredients: "10% Niacinamide + Zinc",
  },
  {
    id: "barrier-cream",
    name: "Ceramide Hydrating Barrier Cream",
    categoryKey: "barrier-cream",
    categoryName: "Barrier Creams",
    price: 52.0,
    rating: 4.8,
    image: serumImg,
    tag: "New",
    ingredients: "5 Ceramides + Fatty Acids",
  },
  {
    id: "gel-moisturizer",
    name: "Dewy Water Lotus Gel Cream",
    categoryKey: "moisturizers",
    categoryName: "Moisturizers",
    price: 44.0,
    rating: 4.9,
    image: serumImg,
    tag: "Oil-Free",
    ingredients: "Lotus Water + Glycerin",
  },
  {
    id: "rice-essence-toner",
    name: "Radiant Rice Essence Toner",
    categoryKey: "cleansers",
    categoryName: "Cleansers & Toners",
    price: 36.0,
    rating: 4.8,
    image: serumImg,
    tag: "Trending",
    ingredients: "77.78% Rice Extract",
  },
];

const Collections: React.FC = () => {
  const { category } = useParams<{ category?: string }>();
  
  const currentKey = category || "serums";
  const info = collectionDetails[currentKey] || collectionDetails.default;

  const [wishlistIds, setWishlistIds] = useState<string[]>([]);
  const [addedCartId, setAddedCartId] = useState<string | null>(null);

  const filteredProducts = category
    ? allCollectionProducts.filter((item) => item.categoryKey === category)
    : allCollectionProducts;

  const toggleWishlist = (id: string): void => {
    setWishlistIds((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  };

  const handleAddToCart = (id: string): void => {
    setAddedCartId(id);
    setTimeout(() => setAddedCartId(null), 2000);
  };

  return (
    <div className="min-h-[calc(100vh-80px)] bg-[#FAF8F5] py-10 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto space-y-10">
        
        {/* Breadcrumb */}
        <div className="flex items-center gap-2 text-xs text-gray-400 font-medium">
          <Link to="/" className="hover:text-black">Home</Link>
          <ChevronRight size={12} />
          <Link to="/collections/serums" className="hover:text-black">Collections</Link>
          <ChevronRight size={12} />
          <span className="text-[#9D7D4F] capitalize">{currentKey.replace("-", " ")}</span>
        </div>

        {/* Hero Banner */}
        <div className="relative rounded-3xl bg-[#1F1B18] text-white p-8 sm:p-12 overflow-hidden border border-[#E8E2D9]">
          <img
            src={serumImg}
            alt="Collection Banner"
            className="absolute inset-0 w-full h-full object-cover opacity-30"
          />
          <div className="relative z-10 max-w-2xl space-y-3">
            <span className="inline-flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-[2px] text-[#9D7D4F] bg-white/10 px-3 py-1 rounded-full border border-white/10">
              <Sparkles size={11} /> {info.bannerTag}
            </span>
            <h1 className="font-serif font-bold text-3xl sm:text-4xl tracking-wide">
              {info.title}
            </h1>
            <p className="text-xs sm:text-sm text-gray-300 font-light leading-relaxed">
              {info.subtitle}
            </p>
          </div>
        </div>

        {/* Category Tabs Switcher */}
        <div className="flex items-center gap-3 overflow-x-auto pb-2 border-b border-[#E8E2D9]">
          {[
            { label: "Serums & Oils", path: "/collections/serums" },
            { label: "Moisturizers", path: "/collections/moisturizers" },
            { label: "Barrier Creams", path: "/collections/barrier-cream" },
            { label: "Cleansers & Toners", path: "/collections/cleansers" },
          ].map((tab) => {
            const isActive = tab.path.endsWith(currentKey);
            return (
              <Link
                key={tab.label}
                to={tab.path}
                className={`text-xs uppercase tracking-wider font-semibold whitespace-nowrap px-4 py-2 rounded-xl transition ${
                  isActive
                    ? "bg-[#1F1B18] text-white shadow-sm"
                    : "bg-white text-gray-600 hover:bg-[#FAF8F5] border border-[#E8E2D9]"
                }`}
              >
                {tab.label}
              </Link>
            );
          })}
        </div>

        {/* Products Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredProducts.map((product) => (
            <div
              key={product.id}
              className="bg-white rounded-2xl border border-[#E8E2D9] overflow-hidden shadow-2xs hover:shadow-md transition flex flex-col justify-between"
            >
              <div className="relative aspect-square bg-[#F7F4EF] overflow-hidden">
                <img
                  src={product.image}
                  alt={product.name}
                  className="w-full h-full object-cover"
                />
                <button
                  onClick={() => toggleWishlist(product.id)}
                  className={`absolute top-3 right-3 p-2 bg-white/90 backdrop-blur-md rounded-full ${
                    wishlistIds.includes(product.id) ? "text-red-500" : "text-gray-400 hover:text-black"
                  }`}
                >
                  <Heart size={15} fill={wishlistIds.includes(product.id) ? "currentColor" : "none"} />
                </button>
              </div>

              <div className="p-5 flex-1 flex flex-col justify-between space-y-4">
                <div>
                  <span className="text-[10px] uppercase tracking-widest text-[#9D7D4F] font-semibold block mb-0.5">
                    {product.categoryName}
                  </span>
                  <h3 className="font-serif font-semibold text-base text-[#1F1B18] line-clamp-1">
                    {product.name}
                  </h3>
                  <p className="text-[11px] text-gray-400 mt-1 italic">{product.ingredients}</p>
                </div>

                <button
                  onClick={() => handleAddToCart(product.id)}
                  className={`w-full py-2.5 rounded-xl text-xs uppercase tracking-widest font-semibold flex items-center justify-center gap-2 transition ${
                    addedCartId === product.id
                      ? "bg-emerald-600 text-white"
                      : "bg-[#1F1B18] hover:bg-[#9D7D4F] text-white"
                  }`}
                >
                  {addedCartId === product.id ? <Check size={14} /> : <ShoppingBag size={14} />}
                  <span>{addedCartId === product.id ? "Added" : "Add to Cart"}</span>
                </button>
              </div>
            </div>
          ))}
        </div>

      </div>
    </div>
  );
};

export default Collections;